import React, { createContext, useContext, useRef, useState } from 'react'

type TabBarContextType = {
  tabBarHeight: number
  setTabBarHeight: (height: number) => void
  /** Callback for visibility changes */
  onTabBarVisibleChange: (visible: boolean, currentScreen?: string) => void
  /** Check if tab bar is visible for lw */
  checkIsTabBarVisibleWeb: () => boolean
  /** Check if tab bar is visible for mobile app */
  checkIsTabBarVisibleMobile: (currentScreen: string) => boolean
}

const TabBarContext = createContext<TabBarContextType | undefined>(undefined)

export const TabBarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tabBarHeight, setTabBarHeight] = useState(0)
  /**
   * Track count of tab bar instances. If we track a simple boolean such as `isTabBarVisible`, there can be a race condition where a component can unmount at the same time another component mounts, so the boolean won't be accurate.
   */
  const [visibilityCount, setVisibilityCount] = useState(0)
  /** Track which screen mounted the tab bar. Used for mobile app. */
  const tabBarMountedByScreen = useRef(new Map())

  const setTabBarVisible = (visible: boolean) => {
    setVisibilityCount((prev) => {
      return visible ? prev + 1 : Math.max(0, prev - 1)
    })
  }

  const setTabBarMountedByScreen = (screen: string | undefined, isVisible: boolean) => {
    if (!screen) {
      return
    }
    tabBarMountedByScreen.current.set(screen, isVisible)
  }

  const checkIsTabBarVisibleMobile = (currentScreen: string) => {
    return Boolean(tabBarMountedByScreen.current.get(currentScreen))
  }

  const checkIsTabBarVisibleWeb = () => {
    return visibilityCount > 0
  }

  const onTabBarVisibleChange = (visible: boolean, currentScreen: string) => {
    setTabBarVisible(visible)
    setTabBarMountedByScreen(currentScreen, visible)
  }

  return (
    <TabBarContext.Provider
      value={{
        tabBarHeight,
        setTabBarHeight,
        onTabBarVisibleChange,
        checkIsTabBarVisibleMobile,
        checkIsTabBarVisibleWeb,
      }}
    >
      {children}
    </TabBarContext.Provider>
  )
}

export const useTabBarVisibility = () => {
  const context = useContext(TabBarContext)
  if (!context) {
    throw new Error('useTabBarVisibility must be used within TabBarProvider')
  }
  return context
}
